import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "426",
  "slug": "geography-trivia-questions-and-answers",
  "title": "Geography Trivia Questions and Answers to Test Your Group",
  "excerpt": "A ready-made bank of geography trivia, sorted from warm-up to brain-buster, with answers and the surprising facts behind them.",
  "date": "2026-06-13",
  "category": "Community",
  "image": "https://images.unsplash.com/photo-1563711246253-b4af863acbd4?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxjb3VudHJ5JTIwZmxhZ3MlMjBjb2xvcmZ1bHxlbnwxfDB8fHwxNzgxMzM4NTUwfDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "A free set of geography trivia questions with answers, grouped by difficulty, ready to drop into a quiz night, classroom, or team meeting. Each answer comes with a little context so the round teaches as well as tests.",
  "content": [
    {
      "type": "paragraph",
      "text": "A good geography round works for almost any group, because everyone has a mental map of the world and everyone has blind spots in it. Below is a ready-to-use bank of questions sorted from gentle openers to genuine head-scratchers. Read them aloud, hand them out, or use them to warm up a room before something bigger. Every answer comes with a little context, so the round teaches as well as tests."
    },
    {
      "type": "heading",
      "text": "Warm-Up Questions"
    },
    {
      "type": "paragraph",
      "text": "Easy openers to get everyone scoring and confident."
    },
    {
      "type": "list",
      "items": [
        "What is the largest country in the world by area? Answer: Russia, by a wide margin.",
        "What is the smallest country in the world? Answer: Vatican City, an enclave inside Rome.",
        "Which continent is the only one with no permanent countries? Answer: Antarctica.",
        "What is the capital of Australia? Answer: Canberra, not Sydney or Melbourne, a classic trap.",
        "How many continents are there? Answer: Seven, by the most common convention."
      ]
    },
    {
      "type": "heading",
      "text": "Medium Questions"
    },
    {
      "type": "paragraph",
      "text": "A step up, where confident guessers start to slip."
    },
    {
      "type": "list",
      "items": [
        "Which country has the most natural lakes? Answer: Canada, home to more lake area than the rest of the world combined.",
        "What is the largest desert on Earth? Answer: Antarctica. A desert is defined by lack of precipitation, not heat, making the frozen continent the biggest one going.",
        "How many countries are there in Africa? Answer: 54.",
        "Which two countries share the longest international border? Answer: Canada and the United States.",
        "What is the most populous country in the world? Answer: India recently overtook China to claim the top spot."
      ]
    },
    {
      "type": "heading",
      "text": "Hard Questions"
    },
    {
      "type": "paragraph",
      "text": "For the players who think they know their atlas."
    },
    {
      "type": "list",
      "items": [
        "Which two countries each border 14 different nations, the most of any country? Answer: China and Russia, tied at 14 neighbours apiece.",
        "What is the lowest exposed point of land on Earth? Answer: The shore of the Dead Sea, more than 400 metres below sea level.",
        "Measured from base to peak rather than from sea level, what is the tallest mountain on Earth? Answer: Mauna Kea in Hawaii, most of which is hidden underwater.",
        "Which country observes the most time zones once its overseas territories are counted? Answer: France, with twelve.",
        "Which African country famously was never formally colonised by a European power? Answer: Ethiopia."
      ]
    },
    {
      "type": "heading",
      "text": "The Tie-Breakers"
    },
    {
      "type": "paragraph",
      "text": "Numerical or surprising questions to settle a draw, where the closest guess wins."
    },
    {
      "type": "list",
      "items": [
        "Roughly what percentage of Earth’s surface is covered by water? Closest answer wins; it is about 71 percent.",
        "What is the longest river in the world? Answer: The Nile is traditionally cited at around 6,650 kilometres, though some studies argue the Amazon is longer, so accept either with an explanation.",
        "What is the world’s largest island? Answer: Greenland.",
        "Which capital city sits at the highest elevation of any in the world? Answer: La Paz, Bolivia, depending on how you define a capital."
      ]
    },
    {
      "type": "heading",
      "text": "Adapting the Round for Your Group"
    },
    {
      "type": "paragraph",
      "text": "The same questions can flex up or down depending on who is playing:"
    },
    {
      "type": "list",
      "items": [
        "For younger players, stick to the warm-up tier and accept any answer in the right region as a near miss.",
        "For competitive adults, make them spell the country or name the capital as well as the country to earn the point.",
        "For mixed groups, run it as teams so stronger and weaker geographers balance each other out.",
        "For a remote meeting, drop the questions into the chat one at a time and have people react with their answers."
      ]
    },
    {
      "type": "heading",
      "text": "How to Run a Great Geography Round"
    },
    {
      "type": "paragraph",
      "text": "A few small choices make trivia land better with any audience:"
    },
    {
      "type": "list",
      "items": [
        "Mix difficulty so beginners stay in the game and experts still get stretched.",
        "Lean on questions with a twist, like the Antarctica desert one, because the surprise is what people remember and retell later.",
        "Add a visual round. Showing a satellite image or an outline and asking players to name the place turns passive trivia into active map-reading.",
        "Keep the answers short but offer the why, so the round teaches as much as it tests."
      ]
    },
    {
      "type": "paragraph",
      "text": "If you want that visual round without building it yourself, EarthGuessr does the work for you: it drops your group into a real satellite view and challenges everyone to pin the spot on a world map. It is the easiest way to turn a flat list of questions into a round people actually cheer for."
    }
  ]
};
